<template>
  <div class="orbital-loader" role="status" :aria-label="label || t('common.loading')">
    <div class="orbital-loader__spinner">
      <div class="orbital-loader__orbit orbital-loader__orbit--1"><span class="orbital-loader__dot"></span></div>
      <div class="orbital-loader__orbit orbital-loader__orbit--2"><span class="orbital-loader__dot"></span></div>
      <div class="orbital-loader__orbit orbital-loader__orbit--3"><span class="orbital-loader__dot"></span></div>
      <div class="orbital-loader__logo">
        <img
          v-if="logoSrc"
          class="orbital-loader__mark"
          :src="logoSrc"
          :alt="logoAlt"
          loading="eager"
          referrerpolicy="no-referrer"
        />
      </div>
    </div>
    <p v-if="label" class="orbital-loader__label">{{ label }}</p>
  </div>
</template>

<script lang="ts">
/**
 * The orbital loader, in the tenant's colours with the tenant's mark at the
 * centre:
 *
 * - Ring/dot/glow colours derive from the TENANT primary via
 *   `--tenant-primary` (guarded to #rrggbb by applyBranding()) through
 *   color-mix — never a hard-coded palette.
 * - The centre mark is the tenant's own logo drawn RAW — no white
 *   rounded-square tile. The tile is the house rule for a mark sitting in a
 *   LIST or a grid cell; a boot splash is a hero, and a white square in the
 *   middle of the orbits reads as a rendering fault. Raw means the variant
 *   must match the page behind it, so the mark follows the THEME here (dark
 *   page -> `logoDarkUrl`) — the opposite of LogoTile, which is always white
 *   and so always light-ink. A white-label shell never flashes the vendor's
 *   mark: the source is the tenant's branding, and nothing renders when it
 *   has none.
 * - 240px: a full-page boot state and an inline one share this component.
 *
 * The pre-resolve splash in index.html carries the same geometry in neutral
 * tones with NO mark — the tenant isn't known yet.
 */
import { computed, defineComponent } from "vue";
import { useTheme } from "vuetify";
import { useI18n } from "vue-i18n";
import { APP_BRANDING, APP_NAME } from "@/app";

export default defineComponent({
  name: "OrbitalLoader",
  props: {
    label: { type: String, default: "" },
  },
  setup() {
    const { t } = useI18n();
    const theme = useTheme();
    const logoAlt = APP_NAME;
    /**
     * The mark is drawn on the page itself, so it takes the variant made for
     * that page: the dark-chrome (light-ink-inverted) mark on a dark theme,
     * the light one otherwise. Either falls back to the other rather than
     * rendering nothing — one variant is better than an empty centre.
     */
    const logoSrc = computed<string | null>(() => {
      const branding = APP_BRANDING;
      if (!branding) return null;
      return theme.global.current.value.dark
        ? branding.logoDarkUrl || branding.logoLightUrl
        : branding.logoLightUrl || branding.logoDarkUrl;
    });
    return { t, logoSrc, logoAlt };
  },
});
</script>

<style scoped>
.orbital-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.orbital-loader__spinner {
  position: relative;
  width: 240px;
  height: 240px;
}

.orbital-loader__orbit {
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 50%;
  border: 2px solid transparent;
}

.orbital-loader__orbit--1 {
  width: 224px;
  height: 224px;
  margin: -112px 0 0 -112px;
  border-top-color: color-mix(in srgb, var(--tenant-primary, #e58ca2) 90%, transparent);
  border-right-color: color-mix(in srgb, var(--tenant-primary, #e58ca2) 30%, transparent);
  animation: standalone-orbit-spin 2s linear infinite;
}

.orbital-loader__orbit--2 {
  width: 180px;
  height: 180px;
  margin: -90px 0 0 -90px;
  border-bottom-color: color-mix(in srgb, var(--tenant-secondary, #89add9) 90%, transparent);
  border-left-color: color-mix(in srgb, var(--tenant-secondary, #89add9) 30%, transparent);
  animation: standalone-orbit-spin 2.5s linear infinite reverse;
}

.orbital-loader__orbit--3 {
  width: 138px;
  height: 138px;
  margin: -69px 0 0 -69px;
  border-top-color: color-mix(in srgb, var(--tenant-primary, #e58ca2) 70%, transparent);
  border-right-color: color-mix(in srgb, var(--tenant-primary, #e58ca2) 20%, transparent);
  animation: standalone-orbit-spin 3s linear infinite;
}

.orbital-loader__dot {
  position: absolute;
  width: 8px;
  height: 8px;
  background: var(--tenant-primary, #e58ca2);
  border-radius: 50%;
  box-shadow: 0 0 12px color-mix(in srgb, var(--tenant-primary, #e58ca2) 90%, transparent);
}

.orbital-loader__orbit--1 .orbital-loader__dot {
  top: -4px;
  left: 50%;
  margin-left: -4px;
}
.orbital-loader__orbit--2 .orbital-loader__dot {
  bottom: -4px;
  left: 50%;
  margin-left: -4px;
}
.orbital-loader__orbit--3 .orbital-loader__dot {
  top: 50%;
  right: -4px;
  margin-top: -4px;
}

.orbital-loader__logo {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  isolation: isolate;
  animation: standalone-orbit-logo-pulse 2s ease-in-out infinite;
}

.orbital-loader__logo::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 90px;
  height: 90px;
  transform: translate(-50%, -50%);
  background: radial-gradient(
    circle,
    color-mix(in srgb, var(--tenant-primary, #e58ca2) 45%, transparent) 0%,
    color-mix(in srgb, var(--tenant-primary, #e58ca2) 15%, transparent) 40%,
    transparent 70%
  );
  border-radius: 50%;
  z-index: -1;
  animation: standalone-orbit-glow-pulse 2s ease-in-out infinite;
}

/* Sized like the root's 115x130 cube, scaled to this 240px spinner (the root's
   is 340px). `contain` inside the box so a wordmark or a square icon both fit
   without distortion — tenant marks are not all the same aspect. */
.orbital-loader__mark {
  display: block;
  position: relative;
  z-index: 1;
  width: 81px;
  height: 92px;
  object-fit: contain;
}

.orbital-loader__label {
  margin: 0;
  font-size: 13px;
  letter-spacing: 0.02em;
  color: color-mix(in srgb, currentColor 72%, transparent);
}

@keyframes standalone-orbit-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes standalone-orbit-logo-pulse {
  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.05);
  }
}

@keyframes standalone-orbit-glow-pulse {
  0%,
  100% {
    opacity: 0.6;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.3);
  }
}
</style>
